// Share a playlist (live shared playlists, Sep 2026 — migrations 0012, 0014).
//
// Signed in, every playlist is already in your account, private. Sharing is a
// switch on it: turned on, its link opens for anyone with access, who then has
// the playlist in their own list, editing the same copy: a tune one person
// adds, or a row moved, reaches everyone, the sharer included
// (src/data/sharedPlaylistSync.ts). The sharer chooses what the link allows,
// Edit by default or View; turned off, the playlist is private again and
// everyone who opened it keeps it as it then stood. Your own saved tunes in it
// are shared from your account (src/data/sharedTunes.ts) so they open for
// everyone too.
//
// Someone who opened the link sees what it lets them do, and the link itself
// to pass on. Renders NOTHING unless the viewer is signed in with access (the
// AccountSection precedent: a feature that does not exist for this viewer).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import type { PlaylistDoc } from '@/storage/playlists';
import { isSharedLink, type ShareAccess } from '@/storage/playlistLinks';
import { APP_ROUTES } from '@/navigation';
import { useAuth } from '@/auth/session';
import { setSharedPlaylistAccess, setlistShareUrl, type OwnShare } from '@/data/sharedSetlists';
import { missingSavedRows, ownTunesIn } from '@/data/sharedTunes';
import {
  followOwnShare,
  refreshSharedPlaylists,
  setPlaylistLinkAccess,
  sharePlaylist,
  stopSharingPlaylist,
  useSharedPlaylistSyncStatus,
} from '@/data/sharedPlaylistSync';
import { useStore } from '@/state/store';
import { recordFeature } from '@/analytics/usage';
import { ToggleSwitch } from './ToggleSwitch';

const ACCESS: readonly [ShareAccess, string][] = [
  ['edit', 'Edit'],
  ['view', 'View'],
];

export function SharePlaylist({ list }: { list: PlaylistDoc }) {
  const auth = useAuth();
  const active = auth.status === 'active';
  const savedCharts = useStore((s) => s.savedCharts);
  const link = useStore((s) => s.playlistLinks[list.id]);
  const linked = !!link;
  const shared = isSharedLink(link);
  const sync = useSharedPlaylistSyncStatus();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [armed, setArmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nothing armed or reported on one playlist carries to the next: an armed
  // Stop sharing handed another playlist would stop sharing that one.
  useEffect(() => {
    setArmed(false);
    setError(null);
  }, [list.id]);

  // A shared playlist checks for changes as it opens; one you shared from
  // another device, or before sharing was live, picks its link back up.
  useEffect(() => {
    if (!active) return;
    if (linked) refreshSharedPlaylists();
    else void followOwnShare(list.id).catch(() => undefined);
  }, [active, linked, list.id]);

  if (!active) return null;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refused (permissions, http): the URL is on screen and
      // selectable, which is the honest fallback.
    }
  };

  const url = link && shared ? setlistShareUrl(link.shareId) : null;
  const own = shared ? 0 : ownTunesIn(list, savedCharts);
  const missing = shared ? 0 : missingSavedRows(list, savedCharts);

  return (
    <div className="playlist-share" data-testid="playlist-share">
      {link && !link.mine ? (
        <span className="playlist-share-role" data-testid="playlist-share-role">
          {link.access === 'edit' ? 'Shared with you · Can edit' : 'Shared with you · View only'}
        </span>
      ) : (
        <>
          <span className="playlist-share-label">Share</span>
          {armed ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              data-testid="playlist-share-stop-confirm"
              autoFocus
              onBlur={() => setArmed(false)}
              onClick={() => void run(() => stopSharingPlaylist(list.id))}
            >
              Stop sharing?
            </button>
          ) : (
            <ToggleSwitch
              on={shared}
              label="Share"
              testid="playlist-share-switch"
              disabled={busy}
              onChange={() =>
                shared
                  ? setArmed(true)
                  : void run(async () => {
                      await sharePlaylist(list);
                      recordFeature('setlist-share');
                    })
              }
            />
          )}
        </>
      )}
      {link?.mine && shared && (
        <span className="playlist-share-access">
          <span className="muted">Anyone with the link can</span>
          <span className="segmented" role="group" aria-label="Anyone with the link can">
            {ACCESS.map(([id, text]) => (
              <button
                key={id}
                type="button"
                className={link.access === id ? 'active' : ''}
                aria-pressed={link.access === id}
                data-testid={`playlist-share-access-${id}`}
                disabled={busy}
                onClick={() =>
                  link.access !== id && void run(() => setPlaylistLinkAccess(list.id, id))
                }
              >
                {text}
              </button>
            ))}
          </span>
        </span>
      )}
      {url && (
        <span className="playlist-share-link">
          <span className="playlist-share-url" data-testid="playlist-share-url">
            {url}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            data-testid="playlist-share-copy"
            onClick={() => void copy(url)}
          >
            {copied ? (
              <Check size={14} aria-hidden="true" />
            ) : (
              <Copy size={14} aria-hidden="true" />
            )}
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </span>
      )}
      {error && (
        <span className="home-load-error" role="alert" data-testid="playlist-share-error">
          {error}
        </span>
      )}
      {!error && linked && sync.phase === 'error' && (
        <span
          className="home-load-error"
          role="alert"
          data-testid="playlist-share-sync-error"
          title={sync.error ?? undefined}
        >
          Not saved yet
        </span>
      )}
      {own > 0 && (
        <span className="muted playlist-share-note" data-testid="playlist-share-own-note">
          {own === 1 ? '1 of your tunes goes with it.' : `${own} of your tunes go with it.`}
        </span>
      )}
      {missing > 0 && (
        <span className="muted playlist-share-note" data-testid="playlist-share-local-note">
          {missing === 1 ? '1 tune is' : `${missing} tunes are`} no longer on this device.
        </span>
      )}
    </div>
  );
}

/**
 * Everything this account has published (#372) — the Playlists index section.
 *
 * PRESENTATIONAL: the index owns the fetch (useOwnShares) because it also
 * tags its own rows from the same list. A share can outlive its local
 * playlist — deleted here, still live for the class — and this list is the
 * only place such a share can be seen or taken down, so an orphan row says
 * so plainly instead of pretending to be openable.
 */
export function SharedWithClass({
  shares,
  error,
  localIds,
  onUnshared,
}: {
  shares: OwnShare[];
  error: string | null;
  localIds: Set<string>;
  onUnshared: (shareId: string) => void;
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [armedId, setArmedId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (shares.length === 0 && !error) return null;

  const copy = async (share: OwnShare) => {
    try {
      await navigator.clipboard.writeText(setlistShareUrl(share.shareId));
      setCopiedId(share.shareId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard refused: nothing sensible to do without a visible URL here.
    }
  };

  const unshare = async (share: OwnShare) => {
    setFailed(null);
    try {
      if (localIds.has(share.playlistId)) await stopSharingPlaylist(share.playlistId);
      else await setSharedPlaylistAccess(share.shareId, 'none');
      onUnshared(share.shareId);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setArmedId(null);
    }
  };

  return (
    <section className="shared-with-class" data-testid="shared-with-class">
      <h2>Shared playlists</h2>
      {(error || failed) && (
        <p className="home-load-error" role="alert">
          {failed ?? error}
        </p>
      )}
      <ul>
        {shares.map((share) => (
          <li key={share.shareId} className="shared-with-class-row">
            {localIds.has(share.playlistId) ? (
              <Link
                className="shared-with-class-name"
                to={`${APP_ROUTES.playlists}/${share.playlistId}`}
              >
                {share.name}
              </Link>
            ) : (
              <span className="shared-with-class-name">
                {share.name} <span className="muted">not on this device</span>
              </span>
            )}
            <span className="muted">
              {share.updatedAt ? new Date(share.updatedAt).toLocaleDateString() : ''}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              data-testid="share-copy"
              aria-label={`Copy the link for ${share.name}`}
              onClick={() => void copy(share)}
            >
              {copiedId === share.shareId ? 'Copied' : 'Copy link'}
            </button>
            {armedId === share.shareId ? (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                data-testid="share-unshare-confirm"
                onBlur={() => setArmedId(null)}
                onClick={() => void unshare(share)}
              >
                Stop sharing?
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                data-testid="share-unshare"
                aria-label={`Stop sharing ${share.name}`}
                onClick={() => setArmedId(share.shareId)}
              >
                Stop sharing
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
